// @ts-nocheck
import { Dialog } from '@headlessui/react';
import React, { useContext, useEffect, useRef } from 'react';

import Transition, {
  MODAL_CONTENT_TRANSITION,
  MODAL_OVERLAY_TRANSITION,
  SIDE_MODAL_CONTENT_TRANSITION,
} from '@/components/Transition';

import { ModalContext } from '../Context';
import { MODAL_ZINDEX_MIN } from '../constants';
import { DEFAULT_MODAL_CONFIG, ModalConfig } from '../useModal';
import _ModalLayer from './_ModalLayer';
import _SideModalLayer from './_SideModalLayer';

export interface ModalProps extends ModalConfig {
  onClose: () => void;
  onOpen?: () => void;
}

const Modal: React.FC<ModalProps> = ({
  onClose,
  onOpen,
  isOverlayClickable,
  size,
  isSide,
  hasCloseButton,
  hasDefaultPadding,
  children,
}) => {
  const { stack } = useContext(ModalContext);

  if (!stack) {
    throw new ReferenceError(
      `'stack' is undefined. Please check 'ModalProvider' is wrapped in.`
    );
  }

  const hasContextModal = stack.length > 0;

  const focusTarget = useRef<HTMLDivElement>(null);

  useEffect(() => {
    onOpen?.();
  }, []);

  return (
    <Transition.Root as={React.Fragment} appear show>
      <Dialog
        as="div"
        initialFocus={hasContextModal ? null : focusTarget}
        style={{ zIndex: MODAL_ZINDEX_MIN - 1 }}
        className="fixed inset-0 overflow-y-auto"
        // open={!hasContextModal}
        static // Whether the element should ignore the internally managed open/closed state.
        onClose={() => {
          // Dialog Outside close event
          if (isOverlayClickable) onClose();
        }}
      >
        {isSide ? (
          <div className="min-h-screen text-right">
            <Transition.Child as={React.Fragment} {...MODAL_OVERLAY_TRANSITION}>
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-10 cursor-pointer" />
            </Transition.Child>
            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="inline-block h-screen align-middle" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child
              as={React.Fragment}
              {...SIDE_MODAL_CONTENT_TRANSITION}
            >
              <_SideModalLayer
                zIndex={MODAL_ZINDEX_MIN}
                size={size}
                hasCloseButton={hasCloseButton}
                hasDefaultPadding={hasDefaultPadding}
                onClose={onClose}
              >
                {({ onClose }) => React.cloneElement(children, { onClose })}
              </_SideModalLayer>
            </Transition.Child>
          </div>
        ) : (
          <div className="min-h-screen px-4 text-center">
            <Transition.Child as={React.Fragment} {...MODAL_OVERLAY_TRANSITION}>
              <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-10 cursor-pointer" />
            </Transition.Child>
            {/* This element is to trick the browser into centering the modal contents. */}
            <span className="inline-block h-screen align-middle" aria-hidden="true">
              &#8203;
            </span>
            <Transition.Child as={React.Fragment} {...MODAL_CONTENT_TRANSITION}>
              <_ModalLayer
                zIndex={MODAL_ZINDEX_MIN}
                size={size}
                hasCloseButton={hasCloseButton}
                hasDefaultPadding={hasDefaultPadding}
                onClose={onClose}
              >
                {({ onClose }) => React.cloneElement(children, { onClose })}
              </_ModalLayer>
            </Transition.Child>
          </div>
        )}
      </Dialog>
    </Transition.Root>
  );
};

Modal.defaultProps = {
  ...DEFAULT_MODAL_CONFIG,
};

export default React.memo(Modal);
